import React, { useState, useEffect } from 'react';
import { Provider } from 'react-redux';
import Amplify from 'aws-amplify';
import { pink, purple, red } from '@material-ui/core/colors';
import { CssBaseline, makeStyles } from '@material-ui/core';
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles';
import { LicenseManager } from 'ag-grid-enterprise';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import { PersistGate } from 'redux-persist/integration/react';
import ElevioElement from 'elevio/lib/react';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { useCookies } from 'react-cookie';
import { AWS_AMPLIFY, COOKIE_OPTIONS } from './config';
import { currentSession, setOrganization, setUser } from './redux/actions/user';
import { fetchMenuLinks } from './services/user';
import Navigation from './components/Navigation';
import SimpleCircularProgress from './components/SimpleCircularProgress';
import store, { persistor } from './redux/store';
import './styles/Notifications.scss';
import Notifications from './components/Notification';
import { ADMIN_MENU_ITEMS, MENU_ITEMS } from './mocks/konami_menu_items';
import { KILLSHOT_ORGANIZATION_COOKIE } from './constants/cookies';

Amplify.configure(AWS_AMPLIFY);

LicenseManager.setLicenseKey(
  'CompanyName=Reps & Company, Inc.,LicensedApplication=Konami,LicenseType=SingleApplication,LicensedConcurrentDeveloperCount=1,LicensedProductionInstancesCount=0,AssetReference=AG-016535,ExpiryDate=28_June_2022_[v2]_MTY1NjM3MDgwMDAwMA==66eb7468d2b14d8702675070c44d981e'
);

// Comment this just in case is needed in the future since we just took the first image for dynamically render the favicon
// const favicon = {
//   nyan: [
//     'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAMAAAAoLQ9TAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAMAUExURQ8YIQwbLgQeNxMXMBMeMh0XOR4fMQ0gPhUiNRUnOyMcOyEpLCEvMCQ+PTgzMAIpSgYqTgcnUAAuWgUuXAorVAsuVAgvVg4rUw0qVwwtUAwvWQ4yTAYzVAEwXgAwXwEyXQAyXgA1WwM0XAE0XwE3XQI3XwcwXgY1XwA4XgoxWgswXAk2Xw4xWQ04WxMpQRUpRBMoVxEtVRUrVBUtWREyXRc5VQAvYAAvYQAsZwAuZwAxYAAwYQExYgAwYwIyYAAyYQAyYwAwZQAxZgEzZAAyZQAyZwE0YQE0YgA0YwE2YwA1ZAI0ZQA0ZgE0ZwA3ZAA3ZQA2ZwQwYQYyYwQyZAUzZQY1YQQ0YgY0Zgc2ZAQ3ZgIxaQAxagEzaAAyaQAyawAzbAAybgA0aAA0aQA1awE2aAI3aQA3agM0bgE2bAE3bQA2bwQwbwc3aAQ3bAU2bgA5YwQ4YAU4ZwU6ZgA4aAM4bAQ4aAQ5awgyZgwyYQ40ZQg4aAA1cAA1cRMpYhQrYRUrZRcsYyMuTi84SSg/XwpWeBlXcCpDWTFDUShTZDVAYFNIG2pOAGFdE05RWE5VXVRKbFRdZExnVltoeXxLT2xDZWNHcHpYc25haG5gbXJveHpueHV2eAtehnFWg3lUh3tWinxXjXpag25jg2Z1iHJihmWXlGmbkn+OpYJZAIRfV4NGf4tOd4FTb5BbiZh9jqZ2nqxxoauocd/OduXKb4aLkYiDmpOEl5KQk5iTl5GVoIi1oa6HnKOTnrWbpMCovtaQzt+W29+f09Soy9qt1N+41+WYxuKd1uWZ1+yc2+yV4/GR6PqY7fmd6v6b6+mszeal2+Wr1OC23uO53euq4PCm4/Sm4Pqh8dLVhODE0wAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAJNhIKMAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAZdEVYdFNvZnR3YXJlAFBhaW50Lk5FVCB2My41LjVJivzgAAABG0lEQVQoUwEQAe/+AHxhJSFMaUVIR05zTVNURGEAe2MjcDtBYm1KSkBNNmw9UABdXDxTUlduOVt0TUtDQEpIAE09XHcYM35/fYAwGCpVSkoAZGJoE4GinZ+gnqGkiRkdZQA7QEQ0j9LP1MbB0MS1LylGAHkmKzKV08nWwKqwwraCNRwACwwNAJTVy8q/ra+WmLmGGwCoi4yKk8XNzKu7rry4tKMuALOy17GpzsjHrL2Xt5uQpwcApaa6kQ7Yw9G+mpmNjpKDMQCchIWIhwYKBQMECAEJAhR4AChvP1EaFxEsKRYVEA8tJz4ASU9eOHc8WTphYTZ6HxJyPwBnQnVJElg8aV5qYzd2O3FHAGtaICQiI0RfYHtfXWZEHlYX2mbiHrA6rAAAAABJRU5ErkJggg==',
//     'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAMAAAAoLQ9TAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAMAUExURQ0dPhQXKBYYPw0kNgghPxInOhAmPRIoPx0mNTcfNTgyGjAlNg4aQhkbSwQoSAcqSAYqTgQvTwksSAUrUgcuVwAuWgEtXgAuXQAvXgAuXwYqXgQtWQsuWAguWw0uWQwuXgMzVwAwWAAxXQIzXAAzXgE1XAM0XQA0XgM0XwE2XgM3XwczWgQzXwY0WAU1XQsyUw0wUAoxWAsyWwsxXgkyXgwzXBEqSRIvTRAvThYoThcrThQsThgjQRwlTBspThIsURAtWRAsWxAvXhstURQwVxAwWRY4VAAvYgAtZgEuZgAvbggrbwIneAAxYQEyYAAyYQAyYwAwZQAwZgAxZwEzZAAyZQIyZgAyZwA0YAI1YgA0YwI3YQA2YwA1ZAE1ZQA0ZgE0ZwA2ZQA2ZgczYgYyYwYxZAUzZAYyZQQyZgQzZwU1ZgY2ZwAxawIyaAAzaQAyawAzbQA0aAA0agE2aAQ2awU0bAI5YgE4Zwc4YQY5ZAA4awM7bgU4aQQ6aQQ8bQk2YAg0ZQk1ZgM0dgM6cgk3cgwxfy82QCw4RjE8WipEXS1JVzRHTiVBZihPbC9SaCZRc0VINVVOMVNePlp2OWtaPkNQWEtUWV5OdVxbYVpib3NPWWtHY2hIbWRdZXhbb3tjS3dmcHl3eGVdhmpeiW1ciGRiimpkimpmiWR0jW91gXtthGqlVWipWYdPfoxSeo1BgpFjipJ6iqB5mLhpqoO0Y52xabPge7ThfIuJlJKCjZqPlZGVmJyTmISKopaMpZKUoKeJobibr7jigM+QyduYwd6c48Sgxt2i0t2h4eWT3e6U1POY0++T6PGT5/SZ6fqc6vqf7v2f9+es2uqm1+O/y/Cyx+Gg5Omj6eit4+un8OW24uSx6fOt4QAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAJj3aiYAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAZdEVYdFNvZnR3YXJlAFBhaW50Lk5FVCB2My41LjVJivzgAAABG0lEQVQoUwEQAe/+AHF3IVtvcHJaJyNjZGVpUF4AYE92JGdnTi4rMhwzZhl8TwBifk8sRT84Nzs6Q0QVfRdZAF9HczQ9p6aio6OkpYwteCQAg29TQpfayNnF1tfbvgURKQBBHjU5nNPO0cmvs8exhkYgAAkLhwGb3M3Mw7CynqC/iS8AlJGSkJrEz9CtwLm8urioNgC2t8K1n9XLyq7BnbuhmL08AKyrtJMK1NLYxqqplZaZiD4Aj42OiosIAg0MAAYDBwRAHwBMhUsaMBITHRsxDg8QFIBWAIJKSHRnFoFrfBh5KGpfTlwAbnt6VmyESVdxTV9fcVVhXABYIlBVbVJ1bV13UVUXWWFiACUqVGBoSXVSUGFXVn8mTXr3Hmu6vgXxNAAAAABJRU5ErkJggg==',
//     'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAMAAAAoLQ9TAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAMAUExURQkbLAEdOQAfPRwcMRIgLh4kKiAhIyQmKTcxIQ0fTwEhQQAqUwArVAAsVgAtWQAuWwAvXA8wUAwxVhwzSQAxYAAyYgAyYwAzZAAzZQA0Zik4Riw5RjM7Ti9AUjhCSzhDW3ZqOElGQkhISFxRRlxUTFFRUVJSUl9ZWWFWSWhWRGZdXWlWaXZYdnhlVHlgX3lkWGVkZHNlaH1uaXN0dXVzc3R0dHh4eDY6vzgzuTk3wCWMPBnGixfBlkHTlpgyCI5Ke7J8fc8wE4BVgKh4gq5vrKiDbcG0Ad/jAefeAfnVEoeFh4qKipKSkpSUlJmZmcuZmdubsPi9ot6F3u+P7/6G6v6G6/+G7P6J7v+L8P+L8f+O9P+P9f+R9/+T+f+U+f+X/P6d8f+Y/P+Y/v+Z//+a/vmkx/mrzv6uzP+m5QAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAE3XPx0AAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAZdEVYdFNvZnR3YXJlAFBhaW50Lk5FVCB2My41LjVJivzgAAAAmklEQVQoU2OQRAMMWAWEdPVAQF9WQlISrEIlPTopLDw5w5UfJpAYGx+s7RLhLC0MVCEoxaicEhUZZOqlY+grJ8ngaKehqBkSl2Tv6+3n623My+Dh7ummkBAT6uSv5WOm7sDNYG1ja8URmJoWYGSipmogI85gaWGuJM/KzMnEwsbAzgW2RYJbVIBPUlKER4xbHGotsmuxO500FQCjIiU3D5OSIgAAAABJRU5ErkJggg==',
//     'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAMAAAAoLQ9TAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAMAUExURQ0dPhQXKBYYPw0kNgghPxInOhAmPRIoPx0mNTcfNTgyGjAlNg4aQhkbSwQoSAcqSAYqTgQvTwksSAUrUgcuVwAuWgEtXgAuXQAvXgAuXwYqXgQtWQsuWAguWw0uWQwuXgMzVwAwWAAxXQIzXAAzXgE1XAM0XQA0XgM0XwE2XgM3XwczWgQzXwY0WAU1XQsyUw0wUAoxWAsyWwsxXgkyXgwzXBEqSRIvTRAvThYoThcrThQsThgjQRwlTBspThIsURAtWRAsWxAvXhstURQwVxAwWRY4VAAvYgAtZgEuZgAvbggrbwIneAAxYQEyYAAyYQAyYwAwZQAwZgAxZwEzZAAyZQIyZgAyZwA0YAI1YgA0YwI3YQA2YwA1ZAE1ZQA0ZgE0ZwA2ZQA2ZgczYgYyYwYxZAUzZAYyZQQyZgQzZwU1ZgY2ZwAxawIyaAAzaQAyawAzbQA0aAA0agE2aAQ2awU0bAI5YgE4Zwc4YQY5ZAA4awM7bgU4aQQ6aQQ8bQk2YAg0ZQk1ZgM0dgM6cgk3cgwxfy82QCw4RjE8WipEXS1JVzRHTiVBZihPbC9SaCZRc0VINVVOMVNePlp2OWtaPkNQWEtUWV5OdVxbYVpib3NPWWtHY2hIbWRdZXhbb3tjS3dmcHl3eGVdhmpeiW1ciGRiimpkimpmiWR0jW91gXtthGqlVWipWYdPfoxSeo1BgpFjipJ6iqB5mLhpqoO0Y52xabPge7ThfIuJlJKCjZqPlZGVmJyTmISKopaMpZKUoKeJobibr7jigM+QyduYwd6c48Sgxt2i0t2h4eWT3e6U1POY0++T6PGT5/SZ6fqc6vqf7v2f9+es2uqm1+O/y/Cyx+Gg5Omj6eit4+un8OW24uSx6fOt4QAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAJj3aiYAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAZdEVYdFNvZnR3YXJlAFBhaW50Lk5FVCB2My41LjVJivzgAAABG0lEQVQoUwEQAe/+AHF3IVtvcHJaJyNjZGVpUF4AYE92JGdnTi4rMhwzZhl8TwBifk8sRT84Nzs6Q0QVfRdZAF9HczQ9p6aio6OkpYwteCQAg29TQpfayNnF1tfbvgURKQBBHjU5nNPO0cmvs8exhkYgAAkLhwGb3M3Mw7CynqC/iS8AlJGSkJrEz9CtwLm8urioNgC2t8K1n9XLyq7BnbuhmL08AKyrtJMK1NLYxqqplZaZiD4Aj42OiosIAg0MAAYDBwRAHwBMhUsaMBITHRsxDg8QFIBWAIJKSHRnFoFrfBh5KGpfTlwAbnt6VmyESVdxTV9fcVVhXABYIlBVbVJ1bV13UVUXWWFiACUqVGBoSXVSUGFXVn8mTXr3Hmu6vgXxNAAAAABJRU5ErkJggg==',
//   ],
// };

datadogLogs.init({
  clientToken: 'pub45945f58e852b8c640d02eeea14b1525',
  site: 'datadoghq.com',
  forwardErrorsToLogs: true,
  sampleRate: 100,
});

const BodyStyles = createGlobalStyle`
  html, body {
    height: 100%;
  }
  body {
    font-family: Nunito Sans,Barlow Condensed,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol";
    display: flex;
    flex-direction: column;
  }
  #app {
    min-height: 100%;
    display: flex;
    flex-direction: column;
  }
`;

const bodyFontFamily = [
  'Nunito Sans',
  'Barlow Condensed',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
  '"Segoe UI Symbol"',
].join(',');

const FAB_POSITION = -20;
const THEME_MODE = 'themeMode';
const THEME_PRIMARY = 'themePrimary';
const THEME_SECONDARY = 'themeSecondary';
const LIGHT_THEME_MODE = 'light';
const DARK_THEME_MODE = 'dark';
const LIGHT_THEME_PRIMARY = purple;
const LIGHT_THEME_SECONDARY = pink;
const DARK_THEME_PRIMARY = { light: purple[100], main: purple[300], dark: purple[500] };
const DARK_THEME_SECONDARY = { light: pink[100], main: pink[300], dark: pink[500] };

const RepsBase = (themeMode, themePrimary, themeSecondary) => ({
  overrides: {
    MuiCollapse: {
      entered: {
        height: 'auto',
        overflow: 'visible',
      },
    },
  },
  palette: {
    primary: themePrimary,
    secondary: themeSecondary,
    error: red,
    type: themeMode,
  },
  typography: {
    fontFamily: bodyFontFamily,
    fontWeightLight: 200,
    fontWeightRegular: 400,
    fontWeightMedium: 600,
    fontWeightBold: 600,
    fontSize: 12,
  },
  mixins: {
    toolbar: {
      minHeight: 45,
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1650,
      xl: 1920,
    },
  },
});

const styles = makeStyles(theme => ({
  footer: {
    backgroundColor: theme.palette.background.paper,
    marginTop: theme.spacing(8),
    padding: `${theme.spacing(6)}px 0`,
  },
  appBar: {
    flexShrink: 0,
  },
  toolbar: {
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  fabButton: {
    position: 'absolute',
    zIndex: 1,
    top: FAB_POSITION,
    left: 0,
    right: 0,
    margin: '0 auto',
  },
  fixed: {
    marginTop: '65px',
  },
  grow: {
    flexGrow: 1,
  },
  content: {
    flex: '1 0 auto',
  },
  contentInside: {
    paddingBottom: '65px',
  },
}));

const App = () => {
  const [isLoading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState('');
  const [themeMode, setThemeMode] = useState(LIGHT_THEME_MODE);
  const [themePrimary, setThemePrimary] = useState(purple);
  const [themeSecondary, setThemeSecondary] = useState(pink);
  const classes = styles();
  const [, setCookie, removeCookie] = useCookies([KILLSHOT_ORGANIZATION_COOKIE]);

  const initUser = async () => {
    try {
      const session = await currentSession();
      if (!session) {
        throw new Error('User not authenticated');
      }
      const response = await fetchMenuLinks();
      const isAdmin = response.is_admin;
      const organizationId = session?.idToken?.payload['custom:organization_id'];
      const userId = session?.idToken?.payload['cognito:username'];
      const userGroup = session?.idToken?.payload['cognito:groups'];

      const userPayload = {
        username: userId,
        token: session.idToken.jwtToken,
        accessToken: session.accessToken.jwtToken,
        isAuthenticated: true,
        email: session.idToken.payload.email,
        menuLinks: isAdmin ? ADMIN_MENU_ITEMS : MENU_ITEMS,
        organizations: response.organizations,
        organizationInfo: {},
        organization_id: organizationId,
        userGroup,
      };

      setUserEmail(session?.idToken?.payload?.email);

      store.dispatch(setUser(userPayload));

      if (response?.organizations?.length) {
        const org = store?.getState()?.user?.organization;

        if (org?.id) {
          store.dispatch(setOrganization(org));
          setCookie(KILLSHOT_ORGANIZATION_COOKIE, org.id, COOKIE_OPTIONS);
        } else {
          store.dispatch(setOrganization(response.organizations[0]));
          setCookie(KILLSHOT_ORGANIZATION_COOKIE, response.organizations[0].i, COOKIE_OPTIONS);
        }
      } else {
        store.dispatch(setOrganization(null));
        removeCookie(KILLSHOT_ORGANIZATION_COOKIE, COOKIE_OPTIONS);
      }
      setLoading(false);
    } catch (e) {
      store.dispatch(
        setUser({
          username: '',
          token: null,
          isAuthenticated: false,
          organization_id: null,
          userInfo: {},
          menuLinks: [],
          organizations: null,
          email: '',
          organizationInfo: {
            name: '',
          },
        })
      );
      setLoading(false);
    }
  };

  const switchThemeMode = () => {
    const currThemeMode = localStorage.getItem(THEME_MODE);

    if (currThemeMode === LIGHT_THEME_MODE) {
      localStorage.setItem(THEME_MODE, DARK_THEME_MODE);
      localStorage.setItem(THEME_PRIMARY, DARK_THEME_PRIMARY);
      localStorage.setItem(THEME_SECONDARY, DARK_THEME_SECONDARY);

      setThemeMode(DARK_THEME_MODE);
      setThemePrimary(DARK_THEME_PRIMARY);
      setThemeSecondary(DARK_THEME_SECONDARY);
    } else {
      localStorage.setItem(THEME_MODE, LIGHT_THEME_MODE);
      localStorage.setItem(THEME_PRIMARY, LIGHT_THEME_PRIMARY);
      localStorage.setItem(THEME_SECONDARY, LIGHT_THEME_SECONDARY);

      setThemeMode(LIGHT_THEME_MODE);
      setThemePrimary(LIGHT_THEME_PRIMARY);
      setThemeSecondary(LIGHT_THEME_SECONDARY);
    }
  };

  const initDatadogRUM = () => {
    datadogRum.init({
      applicationId: process.env.DATADOG_APPLICATION_ID,
      clientToken: process.env.DATADOG_CLIENT_TOKEN,
      site: process.env.DATADOG_SITE,
      service: process.env.DATADOG_SERVICE,
      env: process.env.DATADOG_ENV,
      sampleRate: 100,
      premiumSampleRate: 100,
      trackInteractions: true,
      defaultPrivacyLevel: 'mask-user-input',
      viewTrackingStrategy: 'manual',
    });

    const { user } = store.getState();
    if (user) {
      datadogRum.setUser({
        id: user.username,
        email: user.email,
      });
    }

    datadogRum.startSessionReplayRecording();
  };

  useEffect(() => {
    const mode = localStorage.getItem(THEME_MODE) || LIGHT_THEME_MODE;
    setThemeMode(mode);
    setThemePrimary(mode === LIGHT_THEME_MODE ? LIGHT_THEME_PRIMARY : DARK_THEME_PRIMARY);
    setThemeSecondary(mode === LIGHT_THEME_MODE ? LIGHT_THEME_PRIMARY : DARK_THEME_PRIMARY);
    // eslint-disable-next-line no-console
    console.log('version 1.1.3');

    initUser();

    if (process.env.DATADOG_ENV !== 'local') {
      initDatadogRUM();
    }
  }, []);

  const repsBaseTheme = createTheme(RepsBase(themeMode, themePrimary, themeSecondary));

  if (isLoading) {
    return <SimpleCircularProgress />;
  }

  return (
    <>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <MuiThemeProvider theme={repsBaseTheme}>
            <ThemeProvider theme={repsBaseTheme}>
              <BodyStyles />
              <CssBaseline />
              <div className={classes.content}>
                <Notifications />
                <Navigation userEmail={userEmail} switchThemeMode={switchThemeMode} />
              </div>
              <div>
                <ElevioElement accountId="5fff7d301bc4e" />
              </div>
            </ThemeProvider>
          </MuiThemeProvider>
        </PersistGate>
      </Provider>
    </>
  );
};

export default App;
